/**
 * `/stock-handoff` — open the Stock site signed in as the portal user.
 *
 * Mints a one-time code (POST /api/portal/sso/issue) and replaces this page
 * with Stock's /sso-callback, which exchanges it for a Stock session. Without
 * a portal session the API client sends the browser to
 * /login?redirect=/stock-handoff, so signing in continues here. When the
 * hand-off is unavailable the plain Stock site opens instead.
 */
import { useEffect } from 'react';
import { PortalApiError, apiGet, apiPost } from '../lib/api';
import { getStockFrontendBase } from '../lib/featureFlags';

interface IssueResponse {
  ok: true;
  data: { redirect_url: string };
}

export function StockHandoffPage(): JSX.Element {
  const fallback = `${getStockFrontendBase()}/`;

  useEffect(() => {
    const ctrl = new AbortController();
    void (async () => {
      try {
        // Seeds portal_csrf for the POST below (and bounces to /login without a session).
        await apiGet('/me', ctrl.signal);
        const res = await apiPost<IssueResponse>('/sso/issue', {}, ctrl.signal);
        if (!ctrl.signal.aborted) window.location.replace(res.data.redirect_url);
      } catch (err) {
        // 401: the API client is already on its way to /login?redirect=/stock-handoff.
        if (err instanceof PortalApiError && err.status === 401) return;
        if (!ctrl.signal.aborted) window.location.replace(fallback);
      }
    })();
    return () => ctrl.abort();
  }, [fallback]);

  return (
    <div
      style={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
        textAlign: 'center',
      }}
    >
      <p style={{ margin: 0 }}>
        Opening the Stock site… <a href={fallback}>Continue to the Stock site</a> if nothing
        happens.
      </p>
    </div>
  );
}
